import React, { useEffect, useRef, useState } from 'react';
import { loadSquareSdk } from '@/components/checkout/SquareApplePayButton';

const SQUARE_APP_ID = import.meta.env.VITE_SQUARE_APP_ID || 'sq0idp-HblpXluK_lgmGh2wdXzdCA';
const SQUARE_LOCATION_ID = import.meta.env.VITE_SQUARE_LOCATION_ID || 'LHHYN7QJW6P94';

// Square Web Payments SDK Google Pay button.
//
// Mirrors SquareApplePayButton: loads the SDK on demand, builds a GBP payment
// request, and renders the native Google Pay button on supported browsers.
// When the buyer clicks the button and completes the Google Pay sheet, Square
// tokenizes the payment and fires the 'ontokenization' event — we hand the
// resulting token to the parent via onToken. The parent handles SCA
// verification (squareVerifyBuyer) and the backend charge.
//
// Hidden silently where Google Pay is not available.

export default function SquareGooglePayButton({
  amount,
  label = 'Motion Reality',
  onToken,
  onError,
  validate,
  onAvailabilityChange,
  disabled,
  containerId = 'sq-googlepay-btn',
}) {
  const [available, setAvailable] = useState(false);
  const instanceRef = useRef(null);
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  const validateRef = useRef(validate);
  const onAvailRef = useRef(onAvailabilityChange);
  const disabledRef = useRef(disabled);
  onTokenRef.current = onToken;
  onErrorRef.current = onError;
  validateRef.current = validate;
  onAvailRef.current = onAvailabilityChange;
  disabledRef.current = disabled;

  useEffect(() => {
    let cancelled = false;
    const cleanup = () => {
      if (instanceRef.current) { try { instanceRef.current.destroy(); } catch {} instanceRef.current = null; }
    };
    if (!amount || amount <= 0) { setAvailable(false); cleanup(); return; }

    (async () => {
      try {
        await loadSquareSdk();
        if (cancelled) return;
        const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        const paymentRequest = payments.paymentRequest({
          countryCode: 'GB',
          currencyCode: 'GBP',
          total: { amount: Number(amount).toFixed(2), label },
        });
        const gp = await payments.googlePay(paymentRequest);
        if (cancelled) { try { await gp.destroy(); } catch {} return; }
        const container = document.getElementById(containerId);
        if (!container) { try { await gp.destroy(); } catch {} return; }
        container.innerHTML = '';
        await gp.attach('#' + containerId);
        if (cancelled) { try { await gp.destroy(); } catch {} return; }

        gp.addEventListener('ontokenization', async (e) => {
          if (disabledRef.current) return;
          const { tokenResult } = e.detail;
          if (!tokenResult || tokenResult.status !== 'OK') {
            const msg = tokenResult?.errors?.[0]?.message;
            if (msg && onErrorRef.current) onErrorRef.current(msg);
            return;
          }
          if (typeof validateRef.current === 'function') {
            const err = validateRef.current();
            if (err) { if (onErrorRef.current) onErrorRef.current(err); return; }
          }
          if (onTokenRef.current) onTokenRef.current(tokenResult.token);
        });

        instanceRef.current = gp;
        setAvailable(true);
        if (onAvailRef.current) onAvailRef.current(true);
      } catch {
        if (cancelled) return;
        instanceRef.current = null;
        setAvailable(false);
        if (onAvailRef.current) onAvailRef.current(false);
      }
    })();

    return () => { cancelled = true; cleanup(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, label, containerId]);

  return (
    <div style={{ display: available && !disabled ? 'block' : 'none' }}>
      <div id={containerId} className="min-h-[48px] [&_button]:w-full" />
    </div>
  );
}